// src/composables/usePublicProfileTranslation.js
import { ref } from 'vue'

// Fonction pour détecter automatiquement la langue du navigateur
const detectBrowserLanguage = () => {
  // 1. navigator.language (langue principale)
  const browserLang = navigator.language?.split('-')[0]
  
  // 2. navigator.languages (langues préférées du navigateur)
  const preferredLangs = navigator.languages?.map(lang => lang.split('-')[0])
  
  // 3. Vérifier si une des langues supportées est présente
  const supportedLangs = ['fr', 'en']
  
  // Priorité : langue principale, puis langues préférées, puis français par défaut
  if (browserLang && supportedLangs.includes(browserLang)) {
    return browserLang
  }
  
  if (preferredLangs) {
    for (const lang of preferredLangs) {
      if (supportedLangs.includes(lang)) {
        return lang
      }
    }
  }
  
  // Français par défaut
  return 'fr'
}

// Langue détectée automatiquement
const currentLanguage = ref(detectBrowserLanguage())

// Traductions pour le profil public
const publicTranslations = {
  fr: {
    // Messages d'erreur
    'profile.notAvailable': 'Profil non disponible',
    'profile.error': 'Erreur lors du chargement du profil',
    'profile.loading': 'Chargement...',
    
    // Modal de partage
    'share.title': 'Partager mon neolinks',
    'share.twitter': 'Partager sur Twitter', 
    'share.facebook': 'Partager sur Facebook',
    'share.whatsapp': 'Partager sur WhatsApp',
    'share.copy': 'Copier',
    'share.copied': 'Copié !',
    'share.button': 'Partager',
    
    // Liens et actions
    'profile.email': 'Email',
    'profile.gallery': 'Galerie',
    'profile.customLink': 'Lien personnalisé',
    'profile.verified': 'Vérifié',
    
    // Actions contact
    'contact.save': 'Enregistrer',
    'contact.download': 'Télécharger le contact',
    'contact.saved': 'Contact sauvegardé',
    
    // Modal ouverture liens
    'modal.openWith': 'Ouvrir avec',
    'modal.application': 'Application',
    'modal.browser': 'Navigateur',
    'modal.cancel': 'Annuler',
    
    // Footer
    'footer.createProfile': 'votre profil neolinks.me/vous',
    'footer.create': 'Créer',
    
    // Alternatives textuelles pour les images
    'alt.banner': 'Image de bannière',
    'alt.profile': 'Photo de profil',
    'alt.verified': 'Compte vérifié',
    'alt.qrcode': 'Code QR',
    'alt.gallery': 'Photo de galerie'
  },
  
  en: {
    // Error messages  
    'profile.notAvailable': 'Profile not available',
    'profile.error': 'Error loading profile',
    'profile.loading': 'Loading...',
    
    // Share modal
    'share.title': 'Share my neolinks',
    'share.twitter': 'Share on Twitter',
    'share.facebook': 'Share on Facebook', 
    'share.whatsapp': 'Share on WhatsApp',
    'share.copy': 'Copy',
    'share.copied': 'Copied!',
    'share.button': 'Share',
    
    // Links and actions
    'profile.email': 'Email',
    'profile.gallery': 'Gallery',
    'profile.customLink': 'Custom link',
    'profile.verified': 'Verified',
    
    // Contact actions
    'contact.save': 'Save',
    'contact.download': 'Download contact',
    'contact.saved': 'Contact saved',
    
    // Link opening modal
    'modal.openWith': 'Open with',
    'modal.application': 'Application',
    'modal.browser': 'Browser', 
    'modal.cancel': 'Cancel',
    
    // Footer
    'footer.createProfile': 'your profile neolinks.me/you ',
    'footer.create': 'Create',
    
    // Alternative text for images
    'alt.banner': 'Banner image',
    'alt.profile': 'Profile picture',
    'alt.verified': 'Verified account',
    'alt.qrcode': 'QR Code',
    'alt.gallery': 'Gallery photo'
  }
}

// Fonction pour obtenir une traduction
const getTranslation = (key) => {
  const translation = publicTranslations[currentLanguage.value]?.[key]
  return translation || key // Retourne la clé si traduction introuvable
}

// Hook principal
export function usePublicProfileTranslation() {
  
  // Forcer la détection d'une langue spécifique (optionnel)
  const setLanguage = (lang) => {
    if (['fr', 'en'].includes(lang)) {
      currentLanguage.value = lang
    }
  }
  
  // Fonction de traduction raccourcie
  const t = getTranslation
  
  // Informations sur la langue détectée
  const getLanguageInfo = () => {
    return {
      current: currentLanguage.value,
      detected: detectBrowserLanguage(),
      browser: navigator.language,
      supported: Object.keys(publicTranslations)
    }
  }
  
  return {
    currentLanguage: currentLanguage.value,
    t,
    setLanguage,
    getLanguageInfo
  }
}

// Export des traductions pour utilisation externe si nécessaire
export { publicTranslations }